import { ColumnDef } from "@tanstack/react-table"
import { sortableColumnHeader } from "@/components/data-table"
import { useBillingTranslation } from "@/i18n/hooks"
import { Badge } from "@/components/tailwind/badge"
import { BillingRunItem } from "@/services/billing-services"

function formatAmount(amount: number): string {
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: 'EUR'
  }).format(amount)
}

type TranslationFn = (key: string) => string;

function getPaymentMethodBadge(method: string, t: TranslationFn) {
  const methodConfig = {
    SEPA: { color: 'blue' as const, text: t('paymentMethod.sepa') },
    MANUAL: { color: 'yellow' as const, text: t('paymentMethod.manual') },
    FREE: { color: 'green' as const, text: t('paymentMethod.free') }
  }
  
  const config = methodConfig[method as keyof typeof methodConfig] || { 
    color: 'zinc' as const, 
    text: method 
  }
  
  return <Badge color={config.color}>{config.text}</Badge>
}

function getPaymentStatusBadge(paymentStatus: string, t: TranslationFn) {
  const statusConfig = {
    PENDING: { color: 'yellow' as const, text: t('paymentStatus.pending') },
    PAID: { color: 'green' as const, text: t('paymentStatus.paid') },
    FAILED: { color: 'red' as const, text: t('paymentStatus.failed') },
    CANCELLED: { color: 'zinc' as const, text: t('paymentStatus.cancelled') }
  }

  const config = statusConfig[paymentStatus as keyof typeof statusConfig] || {
    color: 'zinc' as const,
    text: paymentStatus
  }

  return <Badge color={config.color}>{config.text}</Badge>
}

function getFeeTypeDisplay(feeTypeName: string, itemType: string, t: TranslationFn): string {
  if (feeTypeName) {
    return feeTypeName
  }

  switch (itemType) {
    case 'ONE_TIME_DONATION':
      return t('itemType.oneTimeDonation')
    case 'ANNUAL_DONATION':
      return t('itemType.annualDonation')
    default:
      return t('itemType.membershipFee')
  }
}

function FeeTypeCell({ feeTypeName, itemType }: { feeTypeName: string; itemType: string }) {
  const { t } = useBillingTranslation()
  return <>{getFeeTypeDisplay(feeTypeName, itemType, t)}</>
}

function YearHeader() {
  const { t } = useBillingTranslation()
  return <>{t('table.headers.year')}</>
}

function PaymentMethodHeader() {
  const { t } = useBillingTranslation()
  return <>{t('table.headers.paymentMethod')}</>
}

function PaymentMethodCell({ method }: { method: string }) {
  const { t } = useBillingTranslation()
  return getPaymentMethodBadge(method, t)
}

function PaymentStatusHeader() {
  const { t } = useBillingTranslation()
  return <>{t('table.headers.paymentStatus')}</>
}

function PaymentStatusCell({ status }: { status: string }) {
  const { t } = useBillingTranslation()
  return getPaymentStatusBadge(status || 'PENDING', t)
}

export const billingItemColumns: ColumnDef<BillingRunItem>[] = [
    {
      accessorKey: "memberNumber",
      header: ({ column }) => sortableColumnHeader(column, 'table.headers.memberNumber', 'billing'),
      cell: ({ row }) => <span className="font-medium">{row.original.memberNumber}</span>,
    },
    {
      accessorKey: "memberName", 
      header: ({ column }) => sortableColumnHeader(column, 'table.headers.memberName', 'billing'),
      cell: ({ row }) => row.original.memberName,
    },
    {
      accessorKey: "feeTypeName",
      header: ({ column }) => sortableColumnHeader(column, 'table.headers.feeType', 'billing'),
      cell: ({ row }) => <FeeTypeCell feeTypeName={row.original.feeTypeName} itemType={row.original.itemType} />,
    },
    {
      accessorKey: "amount",
      header: ({ column }) => sortableColumnHeader(column, 'table.headers.amount', 'billing'),
      cell: ({ row }) => <span className="font-medium">{formatAmount(row.original.amount)}</span>,
      sortingFn: (rowA, rowB) => rowA.original.amount - rowB.original.amount,
    },
    {
      accessorKey: "year",
      header: () => <YearHeader />,
      cell: ({ row }) => {
        const year = new Date(row.original.periodStart).getFullYear()
        return <span>{year}</span>
      },
      enableSorting: false,
    },
    {
      accessorKey: "paymentMethod",
      header: () => <PaymentMethodHeader />,
      cell: ({ row }) => <PaymentMethodCell method={row.original.paymentMethod} />,
    },
    {
      accessorKey: "paymentStatus",
      header: () => <PaymentStatusHeader />,
      cell: ({ row }) => <PaymentStatusCell status={row.original.paymentStatus || 'PENDING'} />,
    },
]